/**
 * Discreet control to copy or share API correlation diagnostics (flow / trace ids).
 */

import { IonIcon, useIonToast } from "@ionic/react";
import { bugOutline, copyOutline, mailOutline, shareOutline } from "ionicons/icons";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { buildCorrelationDiagnosticReportJson } from "../../services/http/correlationDiagnosticBuffer";
import { getUIColorPalette } from "../../theme/ColorPalette";

interface CorrelationDebugButtonProps {

    /** Dark palette for the trigger pill and modal chrome */
    "isDarkUi": boolean;
}

/**
 * Small bottom-left trigger opening a modal to add a note, then copy or share diagnostics JSON.
 */
const CorrelationDebugButton: React.FC<CorrelationDebugButtonProps> = ( { isDarkUi } ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const [ userNote, setUserNote ] = useState( "" );
    const [ present ] = useIonToast();
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const triggerRef = useRef<HTMLButtonElement | null>( null );

    const buildExportText = useCallback( (): string => {
        const json = buildCorrelationDiagnosticReportJson();
        const trimmed = userNote.trim();

        return trimmed.length > 0 ? `${ trimmed }\n\n${ json }` : json;
    }, [ userNote ] );

    const handleCopy = useCallback( async() => {
        const text = buildExportText();
        try {
            await navigator.clipboard.writeText( text );
            void present( {
                "message":  "Infos de débogage copiées",
                "duration": 2000,
                "position": "bottom",
                "color":    "success",
            } );
        } catch {
            void present( {
                "message":  "Impossible de copier dans le presse-papiers",
                "duration": 2500,
                "position": "bottom",
                "color":    "danger",
            } );
        }
    }, [ buildExportText, present ] );

    const handleShare = useCallback( async() => {
        const text = buildExportText();
        if ( typeof navigator.share === "function" ) {
            try {
                await navigator.share( {
                    "title": "FlowRide diagnostics",
                    text,
                } );
                setIsOpen( false );
            } catch( error: unknown ) {
                const name = error instanceof Error ? error.name : "";
                if ( name === "AbortError" ) {
                    return;
                }
                void present( {
                    "message":  "Échec du partage — essayez Copier ou Mail",
                    "duration": 2500,
                    "position": "bottom",
                    "color":    "warning",
                } );
            }

            return;
        }
        const subject = encodeURIComponent( "Diagnostic FlowRide" );
        const body = encodeURIComponent( text );
        window.location.href = `mailto:?subject=${ subject }&body=${ body }`;
    }, [ buildExportText, present ] );

    const handleOpen = useCallback( () => {
        setIsOpen( true );
    }, [] );

    const handleDismiss = useCallback( () => {
        setIsOpen( false );
    }, [] );

    return (
        <>
            <button
                aria-label="Open diagnostic report"
                className="absolute left-2 z-[5] flex h-7 w-7 items-center justify-center rounded-full border opacity-45 transition-opacity hover:opacity-80 active:opacity-90"
                onClick={handleOpen}
                ref={triggerRef}
                style={{
                    "backgroundColor": palette.overlayCardBackgroundStrong,
                    "borderColor":     palette.overlayBorder,
                    "color":           palette.overlayTextSecondary,
                    "bottom":          "max(8px, env(safe-area-inset-bottom))",
                }}
                title="Diagnostics (flow / trace)"
                type="button"
            >
                <IonIcon
                    aria-hidden="true"
                    className="text-base"
                    icon={bugOutline}
                />
            </button>
            {isOpen && (
                <>
                    <button
                        aria-label="Close diagnostic panel"
                        className="fixed inset-0 z-20 bg-transparent"
                        onClick={handleDismiss}
                        type="button"
                    />
                    <div
                        className="absolute left-2 z-30 w-[320px] max-w-[calc(100vw-1rem)] rounded-2xl border p-3 shadow-lg backdrop-blur-sm"
                        style={{
                            "backgroundColor": palette.overlayCardBackgroundStrong,
                            "borderColor":     palette.overlayBorderStrong,
                            "color":           palette.overlayTextPrimary,
                            "bottom":          "calc(max(8px, env(safe-area-inset-bottom)) + 36px)",
                        }}
                    >
                        <div className="mb-2 flex items-center justify-between">
                            <div className="text-xs font-semibold tracking-wide opacity-90">
                                Infos de débogage
                            </div>
                            <button
                                aria-label="Close diagnostic panel"
                                className="rounded-md px-2 py-1 text-[11px] transition-opacity hover:opacity-80"
                                onClick={handleDismiss}
                                style={{
                                    "backgroundColor": palette.selectorIdleBackground,
                                    "color":           palette.overlayTextSecondary,
                                }}
                                type="button"
                            >
                                Fermer
                            </button>
                        </div>
                        <p
                            className="mb-2 text-[11px] leading-4"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            Ajoutez un court message pour les développeurs (optionnel).
                        </p>
                        <textarea
                            className="mb-3 min-h-[84px] w-full rounded-xl border px-3 py-2 text-xs outline-none"
                            onChange={( e ) => {
                                setUserNote( e.target.value );
                            }}
                            placeholder="Que s'est-il passé ? Quel était le résultat attendu ?"
                            style={{
                                "backgroundColor": palette.overlayCardBackgroundSoft,
                                "borderColor":     palette.overlayBorder,
                                "color":           palette.overlayTextPrimary,
                            }}
                            value={userNote}
                        />
                        <div className="flex flex-wrap gap-2">
                            <button
                                className="inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs font-medium"
                                onClick={() => {
                                    void handleCopy();
                                }}
                                style={{
                                    "backgroundColor": palette.selectorIdleBackground,
                                    "borderColor":     palette.overlayBorder,
                                    "color":           palette.overlayTextPrimary,
                                }}
                                type="button"
                            >
                                <IonIcon icon={copyOutline} />
                                Copier
                            </button>
                            <button
                                className="inline-flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-semibold"
                                onClick={() => {
                                    void handleShare();
                                }}
                                style={{
                                    "backgroundColor": palette.brandPrimary,
                                    "color":           palette.textOnBrandPrimary,
                                }}
                                type="button"
                            >
                                <IonIcon icon={typeof navigator.share === "function" ? shareOutline : mailOutline} />
                                {typeof navigator.share === "function" ? "Partager" : "Mail"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default CorrelationDebugButton;
